// Global music controller — GLM Radio, la radio des artistes SUISSES.
// Playlist dynamique : dernières sorties YouTube des 25 artistes suisses
// suivis (rafraîchie chaque semaine par le classement Top 10 CH), avec
// repli sur une liste suisse codée en dur si l'API est indisponible.

// Repli hors-ligne — uniquement des artistes suisses
const FALLBACK_TRACKS = [
    { type: "yt", id: "kWbCa2a7Vxk", title: "Slimka — WA WA WAR" },
    { type: "yt", id: "mFO0d78QmZ8", title: "Sijay — BPM" },
    { type: "yt", id: "f95Z723TOAI", title: "Sijay — All Eyes On Me" },
    { type: "yt", id: "-SR8qjT5TRs", title: "Yali — East Side" },
    { type: "yt", id: "kAUl3Kz1L7M", title: "Jazzi Sossa — Suki" },
    { type: "yt", id: "5wv8WAtlGx0", title: "Joga Ju — Devagar" },
];

let TRACKS = [...FALLBACK_TRACKS];

// charge la playlist suisse dynamique (chart hebdo) dès que possible
if (typeof window !== "undefined" && process.env.REACT_APP_BACKEND_URL) {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/radio/swiss-tracks`)
        .then((r) => r.json())
        .then((d) => {
            const t = (d.tracks || []).filter((x) => x.type === "yt" && x.id);
            if (t.length >= 4) TRACKS = t;
        })
        .catch(() => {});
}

class MusicController {
    constructor() {
        this.state = { status: "off", title: null, cover: null, muted: false, volume: 0.4 };
        this.listeners = new Set();
        this.yt = null;
        this.ytReady = false;
        this.audio = null;
        this.current = null;
        this.pending = null;
        this._setupMediaSession();
        this._setupVisibilityResume();
    }

    // pré-charge le player YouTube + cale le 1er son SANS le lancer, pour que
    // le tout premier clic joue immédiatement (geste utilisateur encore valide)
    warmup() {
        if (typeof document === "undefined" || this._warmed) return;
        this._warmed = true;
        this.ensureYT(() => {
            try { this.yt.cueVideoById(TRACKS[0].id); } catch { /* ignore */ }
        });
    }

    // smartphone verrouillé : iOS/Android mettent la vidéo en pause.
    // Au déverrouillage (page redevenue visible), on relance le son si l'utilisateur
    // ne l'avait pas mis en pause lui-même.
    _setupVisibilityResume() {
        if (typeof document === "undefined") return;
        document.addEventListener("visibilitychange", () => {
            if (document.hidden || this.state.status !== "playing") return;
            const yt = this._activeYt();
            try {
                if (yt && yt.getPlayerState && yt.getPlayerState() !== 1) yt.playVideo();
            } catch { /* ignore */ }
            if (this.audio && this.audio.paused) this.audio.play().catch(() => {});
        });
    }

    // ---- lock screen / background playback (iOS, Android, desktop) ----
    _setupMediaSession() {
        if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
        try {
            navigator.mediaSession.setActionHandler("play", () => this.toggle());
            navigator.mediaSession.setActionHandler("pause", () => this.toggle());
            navigator.mediaSession.setActionHandler("nexttrack", () => this.next());
            navigator.mediaSession.setActionHandler("previoustrack", () => this.prev());
        } catch {
            /* handlers unsupported — ignore */
        }
    }

    _updateMediaMetadata(track) {
        if (typeof navigator === "undefined" || !("mediaSession" in navigator) || !track) return;
        const cover = track.cover || (track.id ? `https://i.ytimg.com/vi/${track.id}/hqdefault.jpg` : "/img/arrow-3.png");
        navigator.mediaSession.metadata = new MediaMetadata({
            title: track.title || "GLM Radio",
            artist: "GLM RADIO",
            album: "Gloom Ent.",
            artwork: [
                { src: cover, sizes: "480x360", type: "image/jpeg" },
                { src: "/img/arrow-3.png", sizes: "512x512", type: "image/png" },
            ],
        });
    }

    _updatePlaybackState(status) {
        if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
        try {
            navigator.mediaSession.playbackState =
                status === "playing" ? "playing" : status === "paused" ? "paused" : "none";
        } catch {
            /* ignore */
        }
    }

    subscribe(fn) {
        this.listeners.add(fn);
        fn(this.state);
        return () => this.listeners.delete(fn);
    }

    set(patch) {
        this.state = { ...this.state, ...patch };
        this.listeners.forEach((f) => f(this.state));
    }

    ensureYT(onReady) {
        if (this.ytReady) return onReady();
        if (this._loading) {
            this.pending = onReady;
            return;
        }
        this._loading = true;
        const mount = () => {
            let el = document.getElementById("yt-audio-player");
            if (!el) {
                el = document.createElement("div");
                el.id = "yt-audio-player";
                el.setAttribute("data-testid", "yt-audio-player");
                // invisible 1×1 — déplacé dans un conteneur visible quand on lit une vidéo
                el.style.cssText =
                    "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none;";
                document.body.appendChild(el);
            }
            this.yt = new window.YT.Player(el, {
                width: "1",
                height: "1",
                playerVars: { autoplay: 0, controls: 0, disablekb: 1, playsinline: 1 },
                events: {
                    onReady: () => {
                        this.ytReady = true;
                        this._loading = false;
                        onReady();
                        if (this.pending) {
                            const p = this.pending;
                            this.pending = null;
                            p();
                        }
                    },
                    onStateChange: (e) => {
                        // 1 = lecture, 0 = terminé → piste suivante
                        if (e.data === 1) this.set({ status: "playing" });
                        if (e.data === 0) this.next();
                    },
                    onError: () => {
                        // clip not embeddable → skip to another track
                        this.next();
                    },
                },
            });
        };
        if (window.YT && window.YT.Player) {
            mount();
        } else {
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (prev) prev();
                mount();
            };
            if (!document.getElementById("yt-iframe-api")) {
                const tag = document.createElement("script");
                tag.id = "yt-iframe-api";
                tag.src = "https://www.youtube.com/iframe_api";
                document.head.appendChild(tag);
            }
        }
    }

    applyVolume() {
        const v = this.state.muted ? 0 : this.state.volume;
        if (this.audio) this.audio.volume = Math.min(1, v);
        if (this.ytReady && this.yt) this.yt.setVolume(Math.round(v * 100));
    }

    // détruit le player audio caché (évite le conflit de deux instances YouTube)
    _destroyYT() {
        if (this.yt) {
            try { this.yt.destroy(); } catch {}
            this.yt = null;
        }
        this.ytReady = false;
        this._loading = false;
        this.pending = null;
        const el = document.getElementById("yt-audio-player");
        if (el) el.remove();
    }

    _loadYTApi(cb) {
        if (window.YT && window.YT.Player) return cb();
        if (!window.__ytApiLoading) {
            window.__ytApiLoading = true;
            const tag = document.createElement("script");
            tag.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(tag);
        }
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (prev) prev();
            cb();
        };
    }

    // lecteur VIDÉO visible (GLM Radio) — API YouTube Player (autoplay via playVideo
    // au onReady, dans le geste utilisateur). Si YouTube bloque la lecture intégrée
    // (droits musicaux), onError est intercepté → le site affiche un fallback propre.
    openVideo(track, mountId) {
        this.closeVideo(false);
        if (this.audio) { this.audio.pause(); this.audio = null; }
        if (this.ytReady && this.yt) { try { this.yt.stopVideo(); } catch {} }
        this.current = track;
        this._updateMediaMetadata(track);
        this.set({ status: "playing", title: track.title, cover: track.cover || (track.id ? `https://i.ytimg.com/vi/${track.id}/hqdefault.jpg` : null), videoError: false });
        this._updatePlaybackState("playing");
        this._loadYTApi(() => {
            const mount = document.getElementById(mountId);
            if (!mount) return;
            mount.innerHTML = "";
            this.videoYt = new window.YT.Player(mount, {
                width: "100%",
                height: "100%",
                videoId: track.id,
                playerVars: { autoplay: 0, controls: 1, playsinline: 1, rel: 0 },
                events: {
                    onReady: (e) => { try { e.target.playVideo(); } catch {} },
                    onStateChange: (e) => {
                        if (e.data === 1) this.set({ status: "playing", videoError: false });
                        if (e.data === 2 || e.data === 0) this.set({ status: "paused" });
                    },
                    onError: () => {
                        // vidéo refusée par YouTube (droits) → fallback UI + on tente
                        // le SON dans le player caché (certains clips sont lisibles en audio)
                        this.set({ status: "playing", videoError: true });
                        window.dispatchEvent(new CustomEvent("music:video-error", { detail: { id: track.id } }));
                        this.ensureYT(() => {
                            this.yt.loadVideoById(track.id);
                            this.applyVolume();
                            try { this.yt.playVideo(); } catch {}
                        });
                    },
                },
            });
        });
    }

    // ferme la vidéo ; keepAudio=true reprend le son en arrière-plan (player caché)
    closeVideo(keepAudio = false) {
        if (this.videoIframe) {
            try { this.videoIframe.remove(); } catch {}
            this.videoIframe = null;
        }
        if (this.videoYt) {
            try { this.videoYt.destroy(); } catch {}
            this.videoYt = null;
        }
        const mount = document.getElementById("radio-video-mount");
        if (mount) mount.innerHTML = "";
        this.set({ videoError: false });
        if (keepAudio && this.current && this.current.type === "yt-video") {
            const track = { ...this.current, type: "yt" };
            this.current = track;
            this.set({ status: "playing" });
            this.ensureYT(() => {
                this.yt.loadVideoById(track.id);
                this.applyVolume();
                try { this.yt.playVideo(); } catch {}
            });
        } else if (this.current?.type === "yt-video") {
            this.set({ status: "paused" });
        }
    }

    playTrack(track) {
        this.current = track;
        this._updateMediaMetadata(track);
        this.set({ status: "playing", title: track.title, cover: track.cover || (track.id ? `https://i.ytimg.com/vi/${track.id}/hqdefault.jpg` : null) });
        this._updatePlaybackState("playing");
        if (this.audio) {
            this.audio.pause();
            this.audio = null;
        }
        // "yt-video" : la vidéo visible (iframe dédiée) joue le son — on détruit le
        // player audio caché pour éviter le conflit de deux instances YouTube (error 153)
        if (track.type === "yt-video") {
            this._destroyYT();
            return;
        }
        if (track.type === "mp3") {
            if (this.ytReady && this.yt) this.yt.stopVideo();
            const a = new Audio(track.src);
            a.preload = "auto";
            a.volume = this.state.muted ? 0 : this.state.volume;
            a.onended = () => this.next();
            const p = a.play();
            if (p) p.catch(() => {});
            this.audio = a;
        } else {
            // charge la vidéo directement si le player est prêt (pas de race)
            if (this.ytReady && this.yt) {
                this.yt.loadVideoById(track.id);
                this.applyVolume();
                // autoplay peut être bloqué si le player a été déplacé — forcer la lecture
                try { this.yt.playVideo(); } catch {}
                return;
            }
            this.ensureYT(() => {
                this.yt.loadVideoById(track.id);
                this.applyVolume();
                try { this.yt.playVideo(); } catch {}
            });
        }
    }

    toggle() {
        // lecteur vidéo visible actif (GLM Radio) → play/pause direct dessus
        if (this.videoIframe) {
            if (this.state.status === "playing") {
                this._videoCmd("pauseVideo");
            } else {
                this._videoCmd("playVideo");
            }
            return;
        }
        if (this.videoYt) {
            if (this.state.status === "playing") {
                try { this.videoYt.pauseVideo(); } catch {}
            } else {
                try { this.videoYt.playVideo(); } catch {}
            }
            return;
        }
        // "yt-video" sans lecteur actif → relancer la vidéo via l'UI radio
        if (this.current?.type === "yt-video") {
            window.dispatchEvent(new Event("music:yt-video-toggle"));
            return;
        }
        if (this.state.status === "playing") {
            if (this.audio) this.audio.pause();
            if (this.current?.type === "yt" && this.ytReady && this.yt) this.yt.pauseVideo();
            this.set({ status: "paused" });
            this._updatePlaybackState("paused");
        } else if (this.state.status === "paused" && this.current) {
            if (this.audio) this.audio.play().catch(() => {});
            if (this.current.type === "yt" && this.ytReady && this.yt) this.yt.playVideo();
            this.set({ status: "playing" });
            this._updatePlaybackState("playing");
        } else {
            this.playTrack(TRACKS[0]);
        }
    }

    playSpecific(track) {
        this.playTrack(track);
    }

    next() {
        // si on écoute un son spécifique (recherche/artiste), retour à la playlist
        if (this.current && !TRACKS.includes(this.current)) {
            this.playTrack(TRACKS[0]);
            return;
        }
        // sequential — MP3 hébergés d'abord (playlist commence par eux), puis YouTube
        const i = TRACKS.indexOf(this.current);
        const next = TRACKS[(i + 1 + TRACKS.length) % TRACKS.length];
        this.playTrack(next);
    }

    // ---- position / navigation temporelle ----
    _activeYt() {
        return this.videoYt || (this.ytReady ? this.yt : null);
    }

    getTime() {
        if (this.audio) {
            return { t: this.audio.currentTime || 0, d: this.audio.duration || 0 };
        }
        const yt = this._activeYt();
        if (yt && yt.getCurrentTime) {
            try {
                return { t: yt.getCurrentTime() || 0, d: yt.getDuration() || 0 };
            } catch { /* player pas prêt */ }
        }
        return { t: 0, d: 0 };
    }

    seekTo(sec) {
        if (this.audio) {
            this.audio.currentTime = sec;
            return;
        }
        const yt = this._activeYt();
        if (yt && yt.seekTo) {
            try { yt.seekTo(sec, true); } catch { /* ignore */ }
        }
    }

    // bouton précédent : >3s écoulées → remet le son au début, sinon piste précédente
    prev() {
        const { t } = this.getTime();
        if (t > 3) {
            this.seekTo(0);
            return;
        }
        if (this.current && !TRACKS.includes(this.current)) {
            this.playTrack(TRACKS[TRACKS.length - 1]);
            return;
        }
        const i = TRACKS.indexOf(this.current);
        const p = TRACKS[(i - 1 + TRACKS.length) % TRACKS.length];
        this.playTrack(p);
    }

    toggleMute() {
        this.set({ muted: !this.state.muted });
        this.applyVolume();
    }

    setVolume(v) {
        this.set({ volume: v, muted: v === 0 ? this.state.muted : false });
        this.applyVolume();
    }
}

export const music = new MusicController();
